import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react';
import { getActiveWords, getParagraphIndexByTime } from './utils';
import { TranscriptParagraph } from './types';
import { Word } from '@/domains/transcript';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { classnames } from '@/libs/utils';
import { formatTime } from '@/libs/core';
import { remotionPlayerStateStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/remotionPlayerState';
import { useRemotionPlayerCurrentTime } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerCurrentTime';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { getClipFPS } from '@/libs/clipUtils';
import { RemotionPlayerContext } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/context/RemotionPlayerContext';

function JumpToCurrentTime({
  clipId,
  transcript,
  paragraphs,
  fullClipOffset,
  continueAutoScroll,
  virtuosoWrapperRef,
  isSelectingTranscript,
  isDraggingDeletes
}: {
  clipId: string;
  transcript: Record<string, Word[]>;
  paragraphs: TranscriptParagraph[];
  fullClipOffset: number;
  continueAutoScroll: () => void;
  isSelectingTranscript: boolean;
  virtuosoWrapperRef: React.RefObject<HTMLDivElement>;
  isDraggingDeletes: boolean;
}) {
  const { clipData } = useClipsContext();
  const { currentTime } = useRemotionPlayerCurrentTime();
  const { playerRef } = useContext(RemotionPlayerContext);
  const [position, setPosition] = useState<'top' | 'bottom' | null>(null);
  const currentRange = useRef<{ startIndex: number; endIndex: number }>({ startIndex: 0, endIndex: 0 });
  useEffect(() => {
    // We are using timeout to handle case where virtuoso is not yet rendered to newly seeked part.
    // This happens when user seek using timeline.
    let timeoutId: NodeJS.Timeout | null = null;
    const listener = () => {
      const currentTime = remotionPlayerStateStore.getSnapshot().currentTime;
      const { activeWords, currentSegment } = getActiveWords({ currentTime, transcript, clipData });
      if (!currentSegment) {
        setPosition(null);
        return;
      }
      const startTime =
        activeWords.length === 0 ? currentSegment.fromFrame / getClipFPS(clipData) : activeWords[0].start_time;
      const paragraphIndex = getParagraphIndexByTime(startTime, paragraphs, currentSegment.id);
      const virtuosoWrapper = virtuosoWrapperRef.current;
      if (!virtuosoWrapper) {
        return;
      }
      const virtuosoWrapperTop = virtuosoWrapper.getBoundingClientRect().top;
      const virtuosoWrapperBottom = virtuosoWrapper.getBoundingClientRect().bottom;
      // Find the element with data-word-id matching the active word's index
      const activeWordElement = document.querySelector(
        (activeWords.length > 0 ? `[data-word-id="${activeWords[0].index}"]` : '') +
          `[data-segment-id="${currentSegment.id}"]`
      );

      if (paragraphIndex === -1) {
        setPosition(null);
        return;
      }
      if (!activeWordElement) {
        const middleIndex = Math.floor((currentRange.current.startIndex + currentRange.current.endIndex) / 2);
        if (paragraphIndex < middleIndex) {
          setPosition('top');
        } else if (paragraphIndex > middleIndex) {
          setPosition('bottom');
        } else {
          setPosition(null);
        }
      } else {
        const activeWordElementTop = activeWordElement.getBoundingClientRect().top;
        const activeWordElementBottom = activeWordElement.getBoundingClientRect().bottom;

        if (activeWordElementTop >= virtuosoWrapperTop && activeWordElementBottom <= virtuosoWrapperBottom) {
          setPosition(null);
        } else if (activeWordElementTop < virtuosoWrapperTop) {
          setPosition('top');
        } else if (activeWordElementBottom > virtuosoWrapperBottom) {
          setPosition('bottom');
        }
      }
    };
    const callListenerWithTimeout = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        listener();
      }, 100);
    };

    if (playerRef) {
      playerRef.addEventListener('timeupdate', listener);
    }
    const unsubscribe = () => {
      if (playerRef) {
        playerRef.removeEventListener('timeupdate', listener);
      }
    };
    const eventListener = EventBus.on(CustomEvents.FullTranscriptRangeChanged, ({ startIndex, endIndex }) => {
      currentRange.current = { startIndex, endIndex };
      callListenerWithTimeout();
    });

    return () => {
      unsubscribe();
      EventBus.off(CustomEvents.FullTranscriptRangeChanged, eventListener);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [clipData, clipId, fullClipOffset, paragraphs, transcript, virtuosoWrapperRef]);

  if (isSelectingTranscript || position === null || isDraggingDeletes) {
    return;
  }

  return (
    <div
      className={classnames(
        'absolute left-1/2 z-10 flex -translate-x-1/2 cursor-pointer items-center gap-1 space-x-1.5 rounded-full border bg-white/90 px-3 py-2 text-xs font-medium shadow-xl backdrop-blur-sm hover:bg-slate-100',
        position === 'top' ? 'top-1' : '',
        position === 'bottom' ? 'bottom-1' : ''
      )}
      onClick={continueAutoScroll}
    >
      {position === 'top' && <IconArrowUp className="size-4" />}
      Jump to {formatTime(currentTime)}
      {position === 'bottom' && <IconArrowDown className="size-4" />}
    </div>
  );
}

export default memo(JumpToCurrentTime);
